import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HIRING_AI_PROMPT,
  HIRING_MARKDOWN_EXAMPLE,
  PRESS_AI_PROMPT,
  PRESS_MARKDOWN_EXAMPLE,
  applyParsedMarkdownToForm,
  parsePostMarkdown
} from '../../utils/parsePostMarkdown';

/**
 * @param {object} props
 * @param {'hiring' | 'press'} props.variant
 * @param {(updater: (prev: object) => object) => void} props.onApply
 */
const PostMarkdownImport = ({ variant, onApply }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [importError, setImportError] = useState(null);
  const [imported, setImported] = useState(false);
  const isHiring = variant === 'hiring';
  const prompt = isHiring ? HIRING_AI_PROMPT : PRESS_AI_PROMPT;
  const example = isHiring ? HIRING_MARKDOWN_EXAMPLE : PRESS_MARKDOWN_EXAMPLE;
  const exampleName = isHiring ? 'edvise-hiring-opening.example.md' : 'edvise-press-release.example.md';

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
      setImportError(t('postMarkdown.copyFailed'));
    }
  };

  const downloadExample = () => {
    const blob = new Blob([example], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exampleName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parsePostMarkdown(text);
      onApply((prev) => applyParsedMarkdownToForm(prev, parsed));
      setImportError(null);
      setImported(true);
    } catch (err) {
      const code =
        err?.code && ['emptyFile', 'missingFrontmatter', 'noTitle'].includes(err.code)
          ? err.code
          : 'parseError';
      setImported(false);
      setImportError(t(`postMarkdown.errors.${code}`, { defaultValue: t('postMarkdown.errors.parseError') }));
    }
  };

  return (
    <div className="rounded-lg border border-edge bg-canvas p-3 space-y-3">
      <div>
        <p className="text-sm font-semibold text-ink">{t('postMarkdown.title')}</p>
        <p className="mt-1 text-xs text-ink-muted leading-relaxed">{t('postMarkdown.intro')}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copyPrompt}
          className="inline-flex items-center px-3 py-1.5 rounded-md border border-edge-strong text-xs font-medium text-ink bg-surface hover:bg-surface-muted"
        >
          {copied ? t('postMarkdown.copied') : t('postMarkdown.copyPrompt')}
        </button>
        <button
          type="button"
          onClick={downloadExample}
          className="inline-flex items-center px-3 py-1.5 rounded-md border border-edge-strong text-xs font-medium text-ink bg-surface hover:bg-surface-muted"
        >
          {t('postMarkdown.downloadExample')}
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center px-3 py-1.5 rounded-md border border-primary text-xs font-medium text-link bg-surface hover:bg-accent/25"
        >
          {t('postMarkdown.upload')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".md,.markdown,text/markdown,text/plain"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      {importError ? <p className="text-xs text-danger">{importError}</p> : null}
      {imported && !importError ? <p className="text-xs text-emerald-700 dark:text-emerald-300">{t('postMarkdown.imported')}</p> : null}
    </div>
  );
};

export default PostMarkdownImport;
